// History on the Shipping hub: the loads of the last two weeks (Phases 2-4, 2026-10-28; GET /shipping/loads?since=),
// each with who loaded it, the driver / truck, its signature and the manifests (the load's own, and the signed one once
// the receiver signed). Above the old Shipped Items lists; shown when shipping_signing.delivery or .job_loads is on.
import React, { useCallback, useEffect, useState } from 'react';
import { absoluteUrl, clockText, loadManifestPath } from '../signing/signRequestsApi';
import { chipClass, loadDriverText, loadVehicleText, signChips } from '../signing/signWords';
import { HISTORY_DAYS, LOAD_KINDS as KINDS, isExternalUrl, loadRecentLoads } from './shipHistory';

export { HISTORY_DAYS };

const KIND_TEXT = { delivery: 'We deliver', carrier: 'Carrier', job: 'Job load', vendor: 'To a vendor' };

/** One loads row in words: {title, customer, kind, what, who, chips, manifest, signed}. */
export function loadRowView(l, now = new Date()) {
  const jobs = Array.isArray(l.job_numbers) ? l.job_numbers.filter((j) => j != null) : [];
  const title = l.order_number || (jobs.length ? `Job${jobs.length === 1 ? '' : 's'} ${jobs.join(', ')}` : `Load ${l.load_id}`);
  const n = Number(l.pallet_count != null ? l.pallet_count : (Array.isArray(l.pallets) ? l.pallets.length : 0));
  const labels = Array.isArray(l.pallets) ? l.pallets.map((p) => p && (p.label || p.short_id)).filter(Boolean) : [];
  const vehicle = loadVehicleText(l);
  const what = [
    n ? `${n} pallet${n === 1 ? '' : 's'}${labels.length && labels.length <= 3 ? ` (${labels.join(', ')})` : ''}` : null,
    vehicle ? `on ${vehicle}` : null,
    l.destination ? `to ${l.destination}` : null,
    l.loaded_at ? clockText(l.loaded_at, now) : null,
  ].filter(Boolean).join(' · ');
  const driver = loadDriverText(l);
  const who = [l.loaded_by ? `Loaded by ${l.loaded_by}` : null, driver ? `driven by ${driver}` : null].filter(Boolean).join(' · ');
  const sign = l.sign || null;
  const signed = l.signed_manifest_url || l.document_url || (sign && sign.document_url) || null;
  return {
    title,
    customer: l.customer_name || (l.kind === 'job' ? 'Bad Idea' : ''),
    kind: KIND_TEXT[l.kind] || l.kind || '',
    what,
    who,
    chips: signChips(sign ? { ...sign, document_url: signed } : null, { deliveredAt: l.delivered_at || null }),
    manifest: l.manifest_url || (l.load_id != null ? loadManifestPath(l.load_id) : null),
    signed,
  };
}

/** Props: apiBase, ui (openPdfTab). */
export default function LoadsHistory({ apiBase, ui }) {
  const [kind, setKind] = useState('');
  const [state, setState] = useState({ loading: true, loads: [], message: '' });
  const [openError, setOpenError] = useState('');

  const read = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const r = await loadRecentLoads(apiBase, kind);
    setState({ loading: false, loads: r.loads, message: r.message, missing: r.missing });
  }, [apiBase, kind]);

  useEffect(() => { read(); }, [read]);

  async function open(url, filename) {
    setOpenError('');
    const full = absoluteUrl(apiBase, url);
    try {
      if (isExternalUrl(url) || !ui || !ui.openPdfTab) window.open(full, '_blank', 'noopener');
      else await ui.openPdfTab(full, filename);
    } catch (e) {
      setOpenError(`Couldn't open it: ${e.message || e}`);
    }
  }

  if (state.missing) return null; // an API without loads: History is the old lists only
  return (
    <div className="card mb-4" data-testid="hub-loads-history">
      <div className="card-body">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <h5 className="card-title mb-0 me-auto">Loads, last {HISTORY_DAYS} days</h5>
          <select className="form-select" style={{ minHeight: 44, width: 'auto' }} aria-label="Which loads"
            value={kind} onChange={(e) => setKind(e.target.value)}>
            {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
          <button type="button" className="btn btn-outline-secondary" style={{ minHeight: 44 }} onClick={read} disabled={state.loading}>
            ↻ Refresh
          </button>
        </div>
        <div className="small text-muted mb-2">Every load with who put it on the truck, the driver, and the signed manifest.</div>
        {openError && <div className="alert alert-danger py-2">{openError}</div>}
        {state.message && <div className="alert alert-danger py-2">Couldn't load the loads: {state.message}</div>}
        {state.loading && !state.loads.length ? (
          <div className="text-center p-3"><div className="spinner-border text-primary" /></div>
        ) : !state.loads.length ? (
          !state.message && <div className="alert alert-secondary mb-0">No loads in the last {HISTORY_DAYS} days.</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {state.loads.map((l) => {
              const v = loadRowView(l);
              return (
                <div key={l.load_id} className="border rounded p-2 d-flex flex-wrap gap-3 align-items-center" data-testid="hub-load-row">
                  <div style={{ minWidth: 180 }}>
                    <div className="fw-bold" style={{ fontSize: '1.1rem' }}>{v.title}</div>
                    <div>{v.customer}</div>
                    {v.kind && <span className="badge bg-light text-dark border">{v.kind}</span>}
                  </div>
                  <div style={{ flex: '1 1 280px' }}>
                    <div>{v.what}</div>
                    {v.who && <div className="text-muted">{v.who}</div>}
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {v.chips.map((c) => <span key={c.text} className={chipClass(c.tone)}>{c.text}</span>)}
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-1 align-items-end">
                    {v.signed && (
                      <button type="button" className="btn btn-outline-primary" style={{ minHeight: 44 }}
                        onClick={() => open(v.signed, `Signed_Manifest_${v.title}.pdf`)}>
                        Signed manifest
                      </button>
                    )}
                    {v.manifest && (
                      <button type="button" className="btn btn-link p-0" style={{ minHeight: 44 }}
                        onClick={() => open(v.manifest, `Manifest_${v.title}.pdf`)}>
                        Load manifest
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
